import type { PixelBuffer } from './buffer';

/**
 * Tone curves and levels — the two adjustments that work on the *tonemap* rather
 * than on a knob.
 *
 * Both reduce to the same thing: a 256-entry lookup table, input level in, output
 * level out. That is what makes them cheap (one table, built once per parameter
 * change, then a table lookup per channel), what makes them exactly unit-testable,
 * and what lets a preview re-run on every slider tick without a worker.
 *
 * Alpha is never touched, same rule as every other filter here: a tone adjustment
 * must not make a transparent pixel opaque or a visible one vanish.
 */

export interface CurvePoint {
  /** Input position, 0..1. */
  x: number;
  /** Output position, 0..1. */
  y: number;
}

/** The straight line: two endpoints, nothing between them. */
export function identityCurve(): CurvePoint[] {
  return [
    { x: 0, y: 0 },
    { x: 1, y: 1 },
  ];
}

export function curveIsIdentity(points: readonly CurvePoint[]): boolean {
  if (points.length !== 2) return false;
  const [first, last] = points as [CurvePoint, CurvePoint];
  return first.x === 0 && first.y === 0 && last.x === 1 && last.y === 1;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * A curve's control points, normalised.
 *
 * Endpoints keep their x (a curve must span the whole input range) and every point
 * keeps its y inside 0..1. Points closer than 1% in x are pushed apart, because
 * two points at the same x make the interpolation undefined rather than merely
 * ugly. Sorting happens here so nothing downstream has to remember.
 */
export function normaliseCurve(points: readonly CurvePoint[]): CurvePoint[] {
  if (points.length === 0) return identityCurve();
  const sorted = points
    .map((point) => ({ x: clamp01(point.x), y: clamp01(point.y) }))
    .sort((a, b) => a.x - b.x);
  const first = sorted[0] as CurvePoint;
  const last = sorted[sorted.length - 1] as CurvePoint;
  first.x = 0;
  last.x = 1;
  if (sorted.length === 1) return identityCurve();
  for (let i = 1; i < sorted.length - 1; i += 1) {
    const point = sorted[i] as CurvePoint;
    const previous = sorted[i - 1] as CurvePoint;
    if (point.x <= previous.x + 0.01) point.x = Math.min(0.99, previous.x + 0.01);
  }
  return sorted;
}

/**
 * Sample a curve into a 256-entry table.
 *
 * Interpolation is monotone cubic Hermite (Fritsch–Carlson): smooth like a spline,
 * but it cannot overshoot between control points. Overshoot matters here — a
 * Catmull-Rom curve through a steep S can dip below the floor and print a black
 * hole in the shadows that the user never asked for.
 */
export function curveLut(points: readonly CurvePoint[]): Uint8Array {
  const pts = normaliseCurve(points);
  const xs = pts.map((point) => point.x);
  const ys = pts.map((point) => point.y);
  const n = pts.length;

  // Secant slopes between neighbouring points.
  const delta: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    const run = (xs[i + 1] as number) - (xs[i] as number);
    delta.push(run === 0 ? 0 : ((ys[i + 1] as number) - (ys[i] as number)) / run);
  }

  // Tangents, then constrained so the curve stays monotone where the data is.
  const m: number[] = new Array<number>(n).fill(0);
  m[0] = delta[0] ?? 0;
  m[n - 1] = delta[n - 2] ?? 0;
  for (let i = 1; i < n - 1; i += 1) {
    m[i] = ((delta[i - 1] ?? 0) + (delta[i] ?? 0)) / 2;
  }
  for (let i = 0; i < n - 1; i += 1) {
    if ((delta[i] ?? 0) === 0) {
      m[i] = 0;
      m[i + 1] = 0;
    }
  }
  for (let i = 0; i < n - 1; i += 1) {
    const d = delta[i] ?? 0;
    if (d === 0) continue;
    const alpha = (m[i] ?? 0) / d;
    const beta = (m[i + 1] ?? 0) / d;
    const squared = alpha * alpha + beta * beta;
    if (squared > 9) {
      const tau = 3 / Math.sqrt(squared);
      m[i] = tau * alpha * d;
      m[i + 1] = tau * beta * d;
    }
  }

  const lut = new Uint8Array(256);
  let segment = 0;
  for (let level = 0; level < 256; level += 1) {
    const x = level / 255;
    while (segment < n - 2 && x > (xs[segment + 1] as number)) segment += 1;
    const x0 = xs[segment] as number;
    const x1 = xs[segment + 1] as number;
    const y0 = ys[segment] as number;
    const y1 = ys[segment + 1] as number;
    const run = x1 - x0;
    const t = run === 0 ? 0 : (x - x0) / run;
    const t2 = t * t;
    const t3 = t2 * t;
    // Hermite basis.
    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + t;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;
    const value =
      h00 * y0 + h10 * run * (m[segment] ?? 0) + h01 * y1 + h11 * run * (m[segment + 1] ?? 0);
    lut[level] = Math.round(clamp01(value) * 255);
  }
  return lut;
}

/**
 * A levels table: black point, white point, gamma.
 *
 * The classic order — stretch the range first, then bend the midtones — because
 * that is what every levels dialog does and what people expect the sliders to mean.
 * Neutral is 0 / 255 / 1, which is exactly the identity table.
 */
export function levelsLut(black: number, white: number, gamma: number): Uint8Array {
  const floor = Math.min(254, Math.max(0, black));
  const ceiling = Math.max(floor + 1, Math.min(255, white));
  const gammaSafe = Math.min(5, Math.max(0.1, gamma));
  const lut = new Uint8Array(255 + 1);
  const span = ceiling - floor;
  for (let level = 0; level <= 255; level += 1) {
    const stretched = clamp01((level - floor) / span);
    lut[level] = Math.round(clamp01(stretched ** (1 / gammaSafe)) * 255);
  }
  return lut;
}

/** Apply a 256-entry table to the colour channels. Alpha is left alone. */
export function applyLut(buffer: PixelBuffer, lut: Uint8Array): void {
  const { data } = buffer;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = lut[data[i] ?? 0] ?? 0;
    data[i + 1] = lut[data[i + 1] ?? 0] ?? 0;
    data[i + 2] = lut[data[i + 2] ?? 0] ?? 0;
  }
}

/* -------------------------------------------------------------------------- */
/* Editing a curve                                                            */
/* -------------------------------------------------------------------------- */

/** Index of the control point nearest to `point`, or -1 when the curve is empty. */
export function nearestCurvePoint(points: readonly CurvePoint[], point: CurvePoint): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  points.forEach((candidate, index) => {
    const dx = candidate.x - point.x;
    const dy = candidate.y - point.y;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  });
  return best;
}

/**
 * Move a control point.
 *
 * The x of the first and last point is pinned: a curve that does not span the whole
 * input range would silently drop the shadows or the highlights, which is a
 * surprising way to lose data. Their y is free — that is what "lift the blacks" is.
 */
export function moveCurvePoint(
  points: readonly CurvePoint[],
  index: number,
  to: CurvePoint,
): CurvePoint[] {
  const point = points[index];
  if (!point) return [...points];
  const pinnedX = index === 0 || index === points.length - 1;
  const next = points.map((candidate, candidateIndex) =>
    candidateIndex === index
      ? { x: pinnedX ? candidate.x : clamp01(to.x), y: clamp01(to.y) }
      : { ...candidate },
  );
  return normaliseCurve(next);
}

/** Add a control point, or move the one already under the cursor. */
export function addOrMoveCurvePoint(
  points: readonly CurvePoint[],
  at: CurvePoint,
  withinX: number,
): { points: CurvePoint[]; index: number } {
  const index = nearestCurvePoint(points, at);
  const existing = index >= 0 ? points[index] : undefined;
  if (existing) {
    const dx = Math.abs(existing.x - at.x);
    const dy = Math.abs(existing.y - at.y);
    if (dx <= withinX || dy <= withinX) {
      return { points: moveCurvePoint(points, index, at), index };
    }
  }
  const next = normaliseCurve([...points, { x: clamp01(at.x), y: clamp01(at.y) }]);
  return { points: next, index: nearestCurvePoint(next, at) };
}

/** Remove a control point. The two endpoints stay: a curve needs its ends. */
export function removeCurvePoint(points: readonly CurvePoint[], index: number): CurvePoint[] {
  if (points.length <= 2 || index <= 0 || index >= points.length - 1) {
    return [...points];
  }
  return normaliseCurve(points.filter((_, candidateIndex) => candidateIndex !== index));
}
